// escapeConsumption - opt-in Escape "consumed" marker (R3, 2026-07-23).
//
// Replaces the blanket `stopImmediatePropagation()` that DrawerShell used to
// fire whenever its Escape intercept closed an in-drawer panel.
// stopImmediatePropagation silences EVERY other keydown listener on the same
// target - a sledgehammer. Three separate Escape collisions across three
// review rounds traced back to that blanket suppression, most recently it
// starved MissionAnchor's own Escape-dismiss (round 4): MissionAnchor renders
// inside ReflectionDrawer and registered its own document Escape listener,
// which the capture-phase stopImmediatePropagation killed before it could run.
//
// The fix is ownership, not louder suppression. An owner (DrawerShell's
// intercept) marks the specific Escape it fully consumed; ONLY listeners that
// explicitly opt in - currently just the Keyshawn hotkey - check the marker and
// no-op. Nothing else in the app changes behavior. A listener the owner wants
// to STILL fire (e.g. Keyshawn, when the intent is "close Keyshawn, not the
// drawer") is simply left un-marked.
//
// The marker is a property on the SAME event object that travels
// capture → target → bubble, so a capture-phase writer is visible to a later
// bubble-phase reader on the identical dispatch. That ordering guarantee is
// exactly why DrawerShell keeps its Escape listener in the capture phase.
const ESCAPE_CONSUMED_KEY = "__thincccEscapeConsumed";

/**
 * Mark a keydown event as fully consumed by an in-drawer Escape owner. Opt-in
 * global listeners (useKeyshawnHotkey) will no-op on a marked event.
 * @param {Event|null|undefined} event
 */
export function markEscapeConsumed(event) {
  if (event) event[ESCAPE_CONSUMED_KEY] = true;
}

/**
 * Whether a keydown event was already consumed by an in-drawer Escape owner.
 * @param {Event|null|undefined} event
 * @returns {boolean}
 */
export function isEscapeConsumed(event) {
  return Boolean(event && event[ESCAPE_CONSUMED_KEY]);
}

/* ==========================================================================
 * THE DIALOG SURFACE REGISTER (FE-9.2b).
 *
 * The marker above answers "did somebody already handle this Escape". Three
 * listeners in the Facilitator OS needed a second answer first: "is anything
 * in front of me right now", asked BEFORE an Escape arrives or, in one case,
 * about a keystroke that is not Escape at all. All three answered it by
 * sniffing the DOM for `[role="dialog"]`:
 *
 *   - `AssistantPanel` refused to open on Shift-Shift while a dialog was up
 *     (it is z-90 and would have stacked over an open panel with one Escape
 *     between them).
 *   - `QuestionWalk` stood its Space/Enter/Escape gestures down while a dialog
 *     was up, so Escape never meant "skip the walk" from inside a panel.
 *   - Review's narrow workspace declined to consume an Escape while another
 *     dialog was open, excluding itself from the query by node identity.
 *
 * A DOM sniff is the wrong instrument for three reasons and the third is the
 * one that bites. It cannot order what it finds, so "is anything open" can
 * never become "am I the one on top" without a z-index guess. It matches
 * `role="dialog"` nodes this OS does not own and cannot reason about. And it
 * asks the DOM a question the components already know the answer to, which
 * means the answer is only as good as the markup: a panel that renders its
 * `role` conditionally (Review's workspace is a dialog only at narrow widths,
 * `CommitOutcome` only while it holds a credential) is visible or invisible to
 * every other listener depending on a media query.
 *
 * So the surfaces declare themselves. Within a stacking layer, registration
 * order IS stack order, which is true by construction: a panel registers when
 * it opens, and the thing that opened last is the thing in front. Across
 * layers the LAYER decides, because one surface (Review's workspace) joins the
 * stack by a media-query flip rather than by being opened, and joining last is
 * not the same as being in front; see `registerDialogSurface`.
 * `isTopmostDialogSurface` is the query the DOM could not answer.
 *
 * SCOPE, STATED HONESTLY. This register knows about surfaces that opt in. It is
 * not a mirror of every `role="dialog"` node in the app, and it is not trying
 * to be: the student-side drawers and the credential journey overlays are never
 * co-mounted with a facilitator surface, and the two `design-system` overlay
 * components are imported by nothing. What it must stay is EXHAUSTIVE FOR THE
 * OS. A new facilitator overlay that renders a modal panel and does not
 * register is invisible to the three listeners above, and the failure mode is
 * the one the sniff was written to prevent: a gesture that opens a second
 * surface underneath the first. `useDialogSurface` exists so registering is one
 * line, and every OS overlay routes through it.
 * ======================================================================== */
const dialogSurfaces = [];

/**
 * THE STACKING TIERS, NAMED ONCE (FE-9.5).
 *
 * The layer a surface registers with has to be the tier it actually paints on,
 * or the register's answer and the compositor's answer diverge and Escape
 * closes the surface the facilitator cannot see. Three call sites were passing
 * that tier as a bare number, and one of them was passing the wrong one:
 * Prep's apprentice view paints at `z-[95]`, above the assistant's `z-[90]`,
 * and registered on the default frame tier. It then consumed every Escape
 * without asking whether it was in front, which happened to be right only
 * because it never checked. Naming the tiers here is what makes
 * `isTopmostDialogSurface` usable by the apprentice view at all: the value it
 * needs and the class it renders now come from the same line of reasoning.
 *
 * ORB is not a dialog and never registers. It is here because it is the fourth
 * number in the same ladder and the reason it moved: the assistant orb painted
 * at `z-[70]`, above the squad drawer's `z-50`, so it floated over an open
 * drawer directly on top of that drawer's own actions, and clicking it opened
 * a second panel on top of the first. It sits BELOW every dialog tier now, so
 * a modal covers it the way a modal covers everything else.
 */
export const DIALOG_LAYER = {
  /** The shared `Dialog` frame, and the default for anything that omits it. */
  frame: 0,
  /** `AssistantPanel`'s `z-[90]`. */
  assistant: 90,
  /** Prep's `StudentPreviewPanel`, `z-[95]`, in front of the assistant. */
  apprenticeView: 95,
};

/**
 * Declare a node as an open modal surface. WITHIN A LAYER, registration order
 * is stack order; ACROSS layers, the higher layer is in front regardless of
 * when it registered.
 *
 * The layer exists because "the thing that registered last is the thing in
 * front" is true only when every surface joins the stack by being OPENED.
 * One surface in the OS joins it by LAYOUT instead: Review's workspace is a
 * dialog only at narrow widths, so a viewport resize or zoom mounts it while
 * an already-open assistant is still on screen. Registration order then says
 * the workspace is in front; the compositor says the assistant is (it is
 * z-[90] over the frame's z-50), and Escape closed the surface the teacher
 * could not see (9.2b stage-1 carried finding, closed in FE-9.3). The layer is
 * the surface's CSS stacking tier, so the register's answer and the pixels'
 * answer cannot diverge. Default 0 is the shared `Dialog` frame's tier.
 *
 * @param {Element|null|undefined} node
 * @param {number} [layer=0] the surface's stacking tier (assistant: 90)
 * @returns {() => void} unregister, safe to call more than once
 */
export function registerDialogSurface(node, layer = 0) {
  if (!node) return () => {};
  // Re-registering the same node would leave a phantom entry behind after the
  // first unregister and report a closed surface as open forever. React 18
  // StrictMode runs every effect twice in development, so this is the ordinary
  // path in dev, not a defensive branch for an impossible case.
  if (!dialogSurfaces.some((entry) => entry.node === node)) {
    dialogSurfaces.push({ node, layer });
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const at = dialogSurfaces.findIndex((entry) => entry.node === node);
    if (at !== -1) dialogSurfaces.splice(at, 1);
  };
}

/**
 * Is any modal surface open, ignoring one the caller already knows about?
 * @param {Element|null} [exclude] the caller's own node, when it is itself one
 * @returns {boolean}
 */
export function hasOpenDialogSurface(exclude = null) {
  return dialogSurfaces.some((entry) => entry.node !== exclude);
}

/**
 * Is this node the surface in front? False for a node that never registered,
 * which is the correct answer: a surface that did not declare itself cannot
 * claim to be on top of the ones that did. In front means: highest layer, and
 * within that layer, registered latest.
 * @param {Element|null|undefined} node
 * @returns {boolean}
 */
export function isTopmostDialogSurface(node) {
  if (!node || dialogSurfaces.length === 0) return false;
  let front = null;
  for (const entry of dialogSurfaces) {
    // `>=` so a later registration wins a tie: same-layer order is open order.
    if (!front || entry.layer >= front.layer) front = entry;
  }
  return front.node === node;
}

/** Test-only reset. Registration outlives a single render tree; jest does not. */
export function __resetDialogSurfaces() {
  dialogSurfaces.length = 0;
}
